import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import type { IUserInfoResponseData, IWalletBalanceInfo, IUserSubscriptionInfo } from '@/apis/trade/typings';

interface IAssetState {
    userSubscriptionInfo: IUserSubscriptionInfo | null;
    walletBalanceInfo: IWalletBalanceInfo | null;
    isVip: boolean;
    /**
     * 全局「支付成功」信号：finalizePaymentSuccess 完成后更新时间戳。
     * 业务页仅消费做场景逻辑（解锁 / 跳转），公共 refresh / flush / toast / 清活动已在 finalize 完成。
     * 消费后须调用 clearPaymentSuccess，避免卸载后再次进入页面误触发。
     */
    lastPaymentSuccessAt: number;
    /** 充值成功后的首次程序化解锁不展示自动解锁 Toast；由实际解锁方一次性消费。 */
    suppressNextAutoUnlockToast: boolean;
    /**
     * Stripe 弹窗取消后请求展示「放弃支付调研」弹窗的订单 ID。
     * 由 HostedCheckoutReturnConfirmHandler 消费后清空，与 Hosted Checkout 回跳取消共用同一弹窗。
     */
    paymentCancelSurveyOrderId: string;
}

interface IAssetActions {
    setAssetInfo: (data: IUserInfoResponseData) => void;
    clearAssetInfo: () => void;
    markPaymentSuccess: () => void;
    clearPaymentSuccess: () => void;
    /** @returns 本次是否应抑制自动解锁 Toast，并在读取后清除该标记。 */
    consumeAutoUnlockToastSuppression: () => boolean;
    requestPaymentCancelSurvey: (orderId: string) => void;
    clearPaymentCancelSurvey: () => void;
}

const initialState: IAssetState = {
    userSubscriptionInfo: null,
    walletBalanceInfo: null,
    isVip: false,
    lastPaymentSuccessAt: 0,
    suppressNextAutoUnlockToast: false,
    paymentCancelSurveyOrderId: '',
};

export const useAssetStore = create<IAssetState & IAssetActions>()(
    immer(set => ({
        ...initialState,

        setAssetInfo: (data: IUserInfoResponseData) =>
            set({
                userSubscriptionInfo: data.user_subscription_info,
                walletBalanceInfo: data.wallet_balance_info,
                isVip: data.is_vip,
            }),

        clearAssetInfo: () =>
            set({
                userSubscriptionInfo: null,
                walletBalanceInfo: null,
                isVip: false,
            }),

        markPaymentSuccess: () => set({ lastPaymentSuccessAt: Date.now(), suppressNextAutoUnlockToast: true }),
        clearPaymentSuccess: () => set({ lastPaymentSuccessAt: 0 }),
        consumeAutoUnlockToastSuppression: () => {
            let shouldSuppress = false;
            set(state => {
                shouldSuppress = state.suppressNextAutoUnlockToast;
                state.suppressNextAutoUnlockToast = false;
            });
            return shouldSuppress;
        },
        requestPaymentCancelSurvey: (orderId: string) => set({ paymentCancelSurveyOrderId: orderId }),
        clearPaymentCancelSurvey: () => set({ paymentCancelSurveyOrderId: '' }),
    }))
);
